/**
 * Widget styles as a single CSS string.
 *
 * Injected into the Shadow DOM root, so nothing in the customer's
 * page can read or override these rules. Tenant accent color is
 * passed in as a CSS variable so we do not have to template the
 * stylesheet itself.
 *
 * Hand-rolled (no Tailwind / no PostCSS) to keep the gzipped bundle
 * small. CLAUDE.md sectie 4 specifies "no CSS framework" for the
 * widget; this file is the single source of truth for its visuals.
 *
 * Design intent (May 2026 redesign):
 *  - Calm, premium, considered. Linear-style minimalism.
 *  - Voice-first when `tenant.voice_enabled`. Mic is the primary
 *    affordance in the composer; send button only appears when text
 *    is present so the mic stays uncontested.
 *  - Compact panel: 380x560px on desktop, soft bottom-sheet on
 *    mobile. The old 92vh / "almost full viewport" felt oversized.
 *  - The voice modal feels like a phone call (single breathing orb,
 *    audio waveform, big square stop) rather than a Siri overlay.
 *
 * Brand identity tokens come from CLAUDE.md sectie 11 (RespondHero
 * navy / coral / cream system).
 *
 * Typography: Inter is self-hosted at
 * `/v1/fonts/inter-{weight}.woff2` to remove the Google Fonts
 * dependency. The bytes live next to widget.js on Cloudflare Pages,
 * so the font is guaranteed to arrive whenever the widget itself
 * arrives. The previous loader pulled the woff2 from
 * `fonts.gstatic.com`, which a privacy extension or strict CSP on
 * the customer storefront could silently block, leaving the widget
 * rendered in system-ui regardless of the family we declared.
 * Self-hosting also closes the GDPR concern around visitor IPs
 * leaking to Google.
 *
 * The Manrope swap from PR #53 is reversed: the founder's verdict
 * was "lelijk" on both Inter and Manrope, but the actual cause was
 * that fonts.gstatic.com was blocked on his machine so neither
 * family ever rendered. The taste call between Inter and Manrope is
 * unresolved; this PR commits to Inter because it is the marketing-
 * site brand body font (CLAUDE.md sectie 11) and a future taste-only
 * PR can swap it without touching loading semantics.
 *
 * The `@font-face` rules below declare the family inside the shadow
 * root. Shadow-DOM `@font-face` is supported in every modern
 * browser (Chrome 76+, Safari 13+, Firefox 76+). Absolute URLs are
 * used so the rules resolve correctly whether the widget loads via
 * widget.respondhero.com, a future preview deploy, or a localhost
 * dev server with a different origin. Dev preview falls through to
 * system-ui, which is fine because the production path is what
 * visitors hit. `font-display: swap` keeps the first paint instant:
 * the widget renders in the system fallback, then upgrades to Inter
 * once the woff2 arrives, no flash of invisible text.
 *
 * The `--rh-font-display` Georgia stack stays defined for parity
 * with the rest of the design system but is intentionally
 * unreferenced inside the widget surface. Georgia remains the
 * RespondHero brand display font on the marketing site and
 * dashboard.
 */

export const WIDGET_CSS = `
  @font-face {
    font-family: "Inter";
    font-style: normal;
    font-weight: 400;
    font-display: swap;
    src: url("https://widget.respondhero.com/v1/fonts/inter-400.woff2") format("woff2");
  }
  @font-face {
    font-family: "Inter";
    font-style: normal;
    font-weight: 500;
    font-display: swap;
    src: url("https://widget.respondhero.com/v1/fonts/inter-500.woff2") format("woff2");
  }
  @font-face {
    font-family: "Inter";
    font-style: normal;
    font-weight: 600;
    font-display: swap;
    src: url("https://widget.respondhero.com/v1/fonts/inter-600.woff2") format("woff2");
  }
  @font-face {
    font-family: "Inter";
    font-style: normal;
    font-weight: 700;
    font-display: swap;
    src: url("https://widget.respondhero.com/v1/fonts/inter-700.woff2") format("woff2");
  }

  :host {
    all: initial;
    --rh-coral: #FF5C39;
    --rh-coral-light: #FF9E7C;
    --rh-coral-bg: #FFE5DC;
    --rh-coral-soft: #FFF0E8;
    --rh-navy: #0A1628;
    --rh-navy-soft: #1B2A40;
    --rh-cream: #F8F5F0;
    --rh-cream-deep: #F1EBE0;
    --rh-cream-warm: #FAF6EE;
    --rh-white: #FFFFFF;
    --rh-charcoal: #2A2A2A;
    --rh-muted: #8B92A0;
    --rh-muted-soft: #B8BCC4;
    --rh-divider: #ECE7DD;
    --rh-divider-soft: #F4F0E7;
    /* Per-tenant override; falls back to coral when invalid. */
    --rh-accent: var(--rh-coral);
    --rh-shadow:
      0 1px 2px rgba(10, 22, 40, 0.04),
      0 12px 32px rgba(10, 22, 40, 0.10),
      0 24px 56px rgba(10, 22, 40, 0.10);
    --rh-shadow-lift:
      0 1px 2px rgba(10, 22, 40, 0.06),
      0 18px 40px rgba(10, 22, 40, 0.16),
      0 32px 64px rgba(10, 22, 40, 0.12);
    --rh-radius: 20px;
    --rh-radius-bubble: 16px;
    --rh-font-body:
      "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI",
      Roboto, "Helvetica Neue", Arial, sans-serif;
    --rh-font-display:
      Georgia, "Times New Roman", Times, serif;
    font-family: var(--rh-font-body);
    color: var(--rh-navy);
    line-height: 1.5;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }

  *, *::before, *::after { box-sizing: border-box; }

  /* ------------------------------------------------------------------
     Launcher
     A 72px circle with the agent's portrait, a calm cream background,
     a 2px coral ring, and a small green "online" dot at the bottom-
     right rim. Premium widgets (Intercom, Crisp, Drift) lead with a
     real face here because a chat glyph reads as "bot" while a human
     portrait reads as "there is someone here". Restraint over novelty:
     the avatar itself does not animate, only the small online-dot
     pulses subtly so the launcher feels present without competing
     with the storefront content.

     Fallback (no avatar configured): the launcher reverts to the
     coral fill + white chat glyph from the pre-portrait design. The
     .rh-launcher-no-avatar modifier toggles back the solid coral
     surface so a tenant without chatbot_avatar_url still gets a
     branded affordance.
  ------------------------------------------------------------------ */
  .rh-launcher {
    position: fixed;
    right: 24px;
    bottom: 24px;
    width: 72px;
    height: 72px;
    border-radius: 36px;
    overflow: visible;
    padding: 2px;
    border: 2px solid var(--rh-accent);
    background: var(--rh-cream);
    color: var(--rh-white);
    cursor: pointer;
    display: grid;
    place-items: center;
    box-shadow:
      0 1px 2px rgba(10, 22, 40, 0.06),
      0 12px 28px rgba(255, 92, 57, 0.32);
    transition: transform 180ms ease-out, box-shadow 180ms ease-out;
    z-index: 2147483000;
  }
  /* Fallback styling when no portrait is configured: keep the coral
     fill + glyph from the pre-portrait launcher so the affordance
     still reads strongly. */
  .rh-launcher-no-avatar {
    padding: 0;
    border: none;
    background: var(--rh-accent);
  }
  .rh-launcher:hover {
    transform: translateY(-1px);
    box-shadow:
      0 1px 2px rgba(10, 22, 40, 0.08),
      0 16px 32px rgba(255, 92, 57, 0.40);
  }
  .rh-launcher:focus-visible {
    outline: 2px solid var(--rh-accent);
    outline-offset: 4px;
  }
  .rh-launcher svg { width: 24px; height: 24px; }
  .rh-launcher-avatar {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
    display: block;
  }
  /* Online indicator: 12px green dot pinned to the bottom-right rim
     with a 2px white border so it reads cleanly against any avatar
     background. The soft pulse halo reuses the same easing as the
     voice modal's live-dot so the calm-but-present feel is consistent
     across surfaces. RespondHero is 24/7 by claim so the dot is
     always rendered when the panel is closed. */
  .rh-launcher-online-dot {
    position: absolute;
    right: 2px;
    bottom: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #4F9D69;
    border: 2px solid var(--rh-white);
    box-shadow: 0 0 0 3px rgba(79, 157, 105, 0.18);
    animation: rh-live-dot 1600ms ease-in-out infinite;
    pointer-events: none;
  }

  /* ------------------------------------------------------------------
     Panel
     Compact: 380x560 on desktop. Cap at 75vh so a small browser
     window doesn't get a clipped panel. The old design ate ~92vh
     and felt oversized; this footprint sits like a card on the page.
  ------------------------------------------------------------------ */
  .rh-panel {
    position: fixed;
    right: 24px;
    bottom: 96px;
    width: 380px;
    max-width: calc(100vw - 32px);
    height: 560px;
    max-height: min(75vh, 620px);
    background: var(--rh-white);
    border-radius: var(--rh-radius);
    box-shadow: var(--rh-shadow);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2147483000;
    border: 1px solid var(--rh-divider-soft);
    transform-origin: bottom right;
    animation: rh-panel-in 220ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes rh-panel-in {
    from { transform: scale(0.96) translateY(8px); opacity: 0; }
    to { transform: scale(1) translateY(0); opacity: 1; }
  }

  /* Bottom-sheet handle is rendered conditionally on mobile. */
  .rh-panel-handle {
    display: none;
    width: 100%;
    padding: 10px 0 6px;
    place-items: center;
    background: transparent;
    cursor: grab;
  }
  .rh-panel-handle::before {
    content: "";
    width: 36px;
    height: 4px;
    border-radius: 2px;
    background: var(--rh-muted-soft);
  }

  /* ------------------------------------------------------------------
     Header
     Single tight row: brand logo + name + close. ~52px total. Lighter
     than the previous "Bazaaronline / Yara" double-line banner which
     felt corporate.
  ------------------------------------------------------------------ */
  .rh-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    background: var(--rh-white);
    border-bottom: 1px solid var(--rh-divider-soft);
    flex-shrink: 0;
  }
  .rh-header-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .rh-header-mark {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    object-fit: contain;
    flex-shrink: 0;
    background: var(--rh-cream-warm);
    padding: 3px;
  }
  .rh-header-text {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.15;
  }
  .rh-header-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--rh-navy);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    letter-spacing: -0.005em;
  }
  .rh-header-status {
    font-size: 11px;
    color: var(--rh-muted);
    margin-top: 1px;
    display: inline-flex;
    align-items: center;
    gap: 5px;
  }
  .rh-header-status::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #4F9D69;
    box-shadow: 0 0 0 3px rgba(79, 157, 105, 0.18);
  }
  .rh-header-actions {
    display: inline-flex;
    align-items: center;
    gap: 2px;
  }
  .rh-icon-btn {
    background: transparent;
    border: none;
    cursor: pointer;
    color: var(--rh-muted);
    padding: 6px;
    border-radius: 8px;
    display: grid;
    place-items: center;
    transition: background 120ms ease-out, color 120ms ease-out;
  }
  .rh-icon-btn:hover:not(:disabled) {
    color: var(--rh-navy);
    background: var(--rh-cream);
  }
  .rh-icon-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .rh-icon-btn svg { width: 16px; height: 16px; display: block; }

  /* ------------------------------------------------------------------
     Conversation
     Avatar shown only on the first assistant turn of a consecutive run
     so a long thread stays visually calm.
  ------------------------------------------------------------------ */
  .rh-conversation {
    flex: 1;
    overflow-y: auto;
    padding: 18px 16px 6px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: var(--rh-white);
    scrollbar-width: thin;
    scrollbar-color: var(--rh-muted-soft) transparent;
  }
  .rh-conversation::-webkit-scrollbar { width: 6px; }
  .rh-conversation::-webkit-scrollbar-track { background: transparent; }
  .rh-conversation::-webkit-scrollbar-thumb {
    background: var(--rh-muted-soft);
    border-radius: 3px;
  }

  .rh-row { display: flex; align-items: flex-end; gap: 8px; }
  .rh-row-user { justify-content: flex-end; }
  .rh-row-assistant { justify-content: flex-start; }

  .rh-avatar {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--rh-coral-soft);
    color: var(--rh-accent);
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    overflow: hidden;
    user-select: none;
    box-shadow: inset 0 0 0 1px rgba(255, 92, 57, 0.12);
  }
  .rh-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .rh-avatar-spacer { width: 28px; flex-shrink: 0; }

  .rh-bubble {
    max-width: 80%;
    padding: 10px 14px;
    border-radius: var(--rh-radius-bubble);
    /* Body text in the bubble runs at 15px / weight 500 instead of
       the inherited 14px / 400 so the conversation has the same
       presence as the agent name in the header (Inter 600 / 16px).
       Inter at regular weight on 14px tested as too thin for chat
       body copy. The bump keeps the same font for consistency and
       just gives the body line a bit more body. Carried over from
       PR #54 because the 15px / 500 calibration applies cleanly to
       Inter as well. */
    font-size: 15px;
    font-weight: 500;
    line-height: 1.5;
    white-space: pre-wrap;
    word-wrap: break-word;
    position: relative;
  }
  .rh-bubble-user {
    background: var(--rh-navy);
    color: var(--rh-white);
    border-bottom-right-radius: 4px;
  }
  .rh-bubble-assistant {
    background: var(--rh-cream-warm);
    color: var(--rh-navy);
    border: 1px solid var(--rh-divider-soft);
    border-bottom-left-radius: 4px;
  }
  /* Operator-email row: a small label above an assistant-style bubble
     so the visitor reads it as a teammate's reply, not an AI turn.
     No special animation; calm continuity is the brief. */
  .rh-operator-email {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: 100%;
  }
  .rh-operator-email-label {
    font-size: 11px;
    color: var(--rh-muted);
    text-transform: none;
    letter-spacing: 0.01em;
    padding-left: 2px;
  }
  .rh-tool-line {
    font-size: 12px;
    color: var(--rh-muted);
    padding: 0 0 4px 0;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .rh-tool-line::before {
    content: "";
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rh-accent);
    opacity: 0.8;
    animation: rh-tool-pulse 1200ms ease-in-out infinite;
  }
  @keyframes rh-tool-pulse {
    0%, 100% { transform: scale(1); opacity: 0.8; }
    50% { transform: scale(1.4); opacity: 0.3; }
  }

  /* Each delta chunk wraps in this span so it fades in on mount. */
  .rh-token-fade {
    animation: rh-token-fade-in 160ms ease-out both;
  }
  @keyframes rh-token-fade-in {
    from { opacity: 0.3; }
    to { opacity: 1; }
  }

  .rh-source-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 8px;
    padding: 2px 9px;
    border-radius: 999px;
    background: var(--rh-cream);
    color: var(--rh-muted);
    border: 1px solid var(--rh-divider-soft);
    font-size: 11px;
    line-height: 1.4;
  }
  .rh-source-chip-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--rh-accent);
    opacity: 0.8;
  }

  .rh-cursor {
    display: inline-block;
    width: 2px;
    height: 0.95em;
    background: var(--rh-accent);
    margin-left: 2px;
    vertical-align: text-bottom;
    border-radius: 1px;
    animation: rh-cursor-blink 900ms steps(2) infinite;
  }
  @keyframes rh-cursor-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0; }
  }

  .rh-typing-dots {
    display: inline-flex;
    gap: 4px;
    align-items: center;
    padding: 2px 0;
  }
  .rh-typing-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rh-muted);
    opacity: 0.5;
    animation: rh-typing-pulse 1400ms ease-in-out infinite;
  }
  .rh-typing-dots span:nth-child(2) { animation-delay: 200ms; }
  .rh-typing-dots span:nth-child(3) { animation-delay: 400ms; }
  @keyframes rh-typing-pulse {
    0%, 60%, 100% { opacity: 0.4; transform: translateY(0); }
    30% { opacity: 1; transform: translateY(-1px); }
  }

  .rh-error {
    color: #B14B36;
    font-size: 13px;
    padding: 10px 12px;
    background: var(--rh-coral-bg);
    border-radius: 10px;
    margin: 8px 0 0;
    border: 1px solid rgba(177, 75, 54, 0.15);
  }

  /* ------------------------------------------------------------------
     Composer
     Voice-first when mic is present: single row, borderless input,
     mic as a 40px coral pill on the right. Send button materialises
     only when text is typed so the mic stays the primary affordance.
  ------------------------------------------------------------------ */
  .rh-composer-wrap {
    flex-shrink: 0;
    background: var(--rh-white);
    border-top: 1px solid var(--rh-divider-soft);
    padding: 10px 12px 8px;
  }
  .rh-composer {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 6px 6px 6px 14px;
    background: var(--rh-cream-warm);
    border: 1px solid var(--rh-divider-soft);
    border-radius: 18px;
    transition: border-color 140ms ease-out, background 140ms ease-out;
  }
  .rh-composer:focus-within {
    border-color: var(--rh-coral-light);
    background: var(--rh-white);
    box-shadow: 0 0 0 3px rgba(255, 92, 57, 0.10);
  }
  .rh-composer textarea {
    flex: 1;
    resize: none;
    min-height: 32px;
    max-height: 120px;
    border: none;
    padding: 6px 0;
    font: inherit;
    font-size: 14px;
    line-height: 1.45;
    color: var(--rh-navy);
    background: transparent;
    outline: none;
  }
  .rh-composer textarea:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .rh-composer textarea::placeholder { color: var(--rh-muted); }

  /* Primary mic button: voice-first weight when text is empty. The
     coral fill makes the affordance unmistakable; size + shadow give
     it presence without being shouty. */
  .rh-mic-primary {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: var(--rh-accent);
    color: var(--rh-white);
    cursor: pointer;
    display: grid;
    place-items: center;
    box-shadow:
      0 1px 2px rgba(10, 22, 40, 0.06),
      0 6px 14px rgba(255, 92, 57, 0.30);
    transition: transform 140ms ease-out, box-shadow 140ms ease-out, filter 140ms ease-out;
  }
  .rh-mic-primary:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow:
      0 2px 4px rgba(10, 22, 40, 0.08),
      0 10px 20px rgba(255, 92, 57, 0.38);
  }
  .rh-mic-primary:active:not(:disabled) { transform: translateY(0); }
  .rh-mic-primary:disabled { opacity: 0.45; cursor: not-allowed; box-shadow: none; }
  .rh-mic-primary svg { width: 18px; height: 18px; }

  /* Send button: appears only when draft is non-empty. Replaces the
     mic in the composer's right slot, so the right-side affordance is
     always exactly one button. */
  .rh-send {
    flex-shrink: 0;
    width: 40px;
    height: 40px;
    border: none;
    border-radius: 50%;
    background: var(--rh-navy);
    color: var(--rh-white);
    cursor: pointer;
    display: grid;
    place-items: center;
    transition: filter 120ms ease-out, transform 120ms ease-out;
    animation: rh-send-in 160ms cubic-bezier(0.22, 1, 0.36, 1);
  }
  @keyframes rh-send-in {
    from { transform: scale(0.85); opacity: 0; }
    to { transform: scale(1); opacity: 1; }
  }
  .rh-send:hover:not(:disabled) { filter: brightness(1.18); }
  .rh-send:disabled { opacity: 0.45; cursor: not-allowed; }
  .rh-send svg { width: 18px; height: 18px; transform: translateX(1px) translateY(-1px); }

  /* ------------------------------------------------------------------
     Attach photo button + composer attachments tray
     The paperclip lives in the composer's left edge so the right slot
     stays the single primary affordance (mic or send). The tray sits
     just above the composer and shows thumbnails for any attachments
     the operator has picked but not yet sent.
  ------------------------------------------------------------------ */
  .rh-attach-btn {
    flex-shrink: 0;
    background: transparent;
    border: none;
    color: var(--rh-muted);
    cursor: pointer;
    padding: 8px;
    margin: 0 -2px 0 -6px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    transition: background 120ms ease-out, color 120ms ease-out;
  }
  .rh-attach-btn:hover:not(:disabled) {
    color: var(--rh-navy);
    background: var(--rh-cream);
  }
  .rh-attach-btn:disabled { opacity: 0.4; cursor: not-allowed; }
  .rh-attach-btn svg { width: 18px; height: 18px; display: block; }
  .rh-attach-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .rh-pending-tray {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 6px 4px 8px;
  }
  .rh-thumb {
    position: relative;
    width: 56px;
    height: 56px;
    border-radius: 10px;
    overflow: hidden;
    background: var(--rh-cream-warm);
    border: 1px solid var(--rh-divider-soft);
    flex-shrink: 0;
  }
  .rh-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .rh-thumb-overlay {
    position: absolute;
    inset: 0;
    background: rgba(10, 22, 40, 0.45);
    display: grid;
    place-items: center;
    color: var(--rh-white);
    font-size: 9px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    text-align: center;
    padding: 4px;
    line-height: 1.2;
  }
  .rh-thumb-remove {
    position: absolute;
    top: 2px;
    right: 2px;
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: rgba(10, 22, 40, 0.7);
    color: var(--rh-white);
    border: none;
    cursor: pointer;
    display: grid;
    place-items: center;
    padding: 0;
    line-height: 1;
    font-size: 12px;
  }
  .rh-thumb-remove:hover { background: var(--rh-navy); }
  .rh-thumb-failed {
    border-color: rgba(177, 75, 54, 0.45);
  }
  .rh-thumb-failed .rh-thumb-overlay {
    background: rgba(177, 75, 54, 0.55);
  }
  .rh-thumb-error {
    font-size: 11.5px;
    color: #B14B36;
    padding: 4px 6px 0;
    line-height: 1.35;
  }
  .rh-thumb-retry {
    background: transparent;
    border: none;
    color: var(--rh-accent);
    cursor: pointer;
    font: inherit;
    font-size: 11.5px;
    padding: 0 0 0 4px;
    text-decoration: underline;
  }

  /* In-thread attachments live above the bubble text. Same border
     radius as the bubble, no extra surface, so multiple photos read
     as one cluster rather than a gallery. */
  .rh-bubble-thumbs {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 6px;
  }
  .rh-bubble-thumbs .rh-thumb { width: 80px; height: 80px; border-radius: 12px; }

  /* Disclaimer: small, low-contrast, single line under the composer.
     Hallon compliance still requires it visible; the visual weight is
     just rebalanced down. */
  .rh-disclaimer {
    font-size: 10.5px;
    color: var(--rh-muted);
    text-align: center;
    padding: 6px 4px 0;
    line-height: 1.4;
    margin: 0;
    letter-spacing: 0.01em;
  }

  /* ------------------------------------------------------------------
     Welcome state
     Calm intro surface shown when the panel first opens. Three
     stacked cards on a cream background: greeting + powered-by,
     chat-with-agent + CTA, direct-contact pills. Visitor enters
     chat by tapping the CTA; back-arrow on the chat header brings
     them back here. The × everywhere closes the whole widget.
  ------------------------------------------------------------------ */
  .rh-panel-welcome {
    background: var(--rh-cream);
  }
  .rh-welcome {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
  }
  .rh-welcome-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    flex-shrink: 0;
    background: transparent;
  }
  .rh-welcome-header-brand {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-width: 0;
  }
  .rh-welcome-header-mark {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    object-fit: contain;
    background: var(--rh-white);
    padding: 3px;
    flex-shrink: 0;
  }
  .rh-welcome-header-mark-fallback {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: var(--rh-white);
    color: var(--rh-navy);
    display: grid;
    place-items: center;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
  }
  .rh-welcome-header-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--rh-navy);
    letter-spacing: -0.005em;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .rh-welcome-body {
    flex: 1;
    overflow-y: auto;
    padding: 4px 16px 18px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    scrollbar-width: thin;
    scrollbar-color: var(--rh-muted-soft) transparent;
  }
  .rh-welcome-body::-webkit-scrollbar { width: 6px; }
  .rh-welcome-body::-webkit-scrollbar-thumb {
    background: var(--rh-muted-soft);
    border-radius: 3px;
  }

  .rh-welcome-greeting {
    background: var(--rh-white);
    border: 1px solid var(--rh-divider-soft);
    border-radius: 16px;
    padding: 20px 20px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: rh-welcome-card-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  .rh-welcome-greeting-line {
    font-size: 22px;
    line-height: 1.25;
    color: var(--rh-navy);
    margin: 0;
    font-weight: 600;
    letter-spacing: -0.01em;
  }
  .rh-welcome-powered-by {
    margin: 0;
    font-size: 11px;
    color: var(--rh-muted);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    letter-spacing: 0.01em;
  }
  .rh-welcome-coral-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rh-accent);
    display: inline-block;
    flex-shrink: 0;
  }

  .rh-welcome-chat-card {
    background: var(--rh-white);
    border: 1px solid var(--rh-divider-soft);
    border-radius: 16px;
    padding: 20px 20px 18px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    animation: rh-welcome-card-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both 60ms;
  }
  .rh-welcome-chat-card-row {
    display: flex;
    align-items: flex-start;
    gap: 16px;
  }
  .rh-welcome-chat-avatar {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: var(--rh-coral-soft);
    color: var(--rh-accent);
    display: grid;
    place-items: center;
    font-size: 26px;
    font-weight: 600;
    flex-shrink: 0;
    overflow: hidden;
    box-shadow:
      inset 0 0 0 1px rgba(255, 92, 57, 0.12),
      0 2px 8px rgba(255, 92, 57, 0.10);
  }
  .rh-welcome-chat-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .rh-welcome-chat-text {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }
  .rh-welcome-chat-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--rh-navy);
    margin: 0;
    line-height: 1.3;
  }
  .rh-welcome-chat-intro {
    font-size: 13px;
    line-height: 1.5;
    color: var(--rh-charcoal);
    margin: 0;
    opacity: 0.88;
  }
  .rh-welcome-cta {
    background: var(--rh-navy);
    color: var(--rh-white);
    border: none;
    border-radius: 12px;
    padding: 12px 16px;
    font: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 140ms ease-out, transform 140ms ease-out;
    width: 100%;
    text-align: center;
    letter-spacing: 0.005em;
  }
  .rh-welcome-cta:hover { filter: brightness(1.16); }
  .rh-welcome-cta:active { transform: translateY(1px); }
  .rh-welcome-cta:focus-visible {
    outline: 2px solid var(--rh-accent);
    outline-offset: 3px;
  }

  .rh-welcome-channels {
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: rh-welcome-card-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both 120ms;
  }
  .rh-welcome-channels-heading {
    margin: 4px 0 2px 4px;
    font-family: var(--rh-font-body);
    font-size: 10.5px;
    font-weight: 600;
    color: var(--rh-muted);
    text-transform: uppercase;
    letter-spacing: 0.10em;
  }
  .rh-welcome-channel-list {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
  }
  .rh-welcome-channel {
    background: var(--rh-white);
    border: 1px solid var(--rh-divider-soft);
    border-radius: 12px;
    overflow: hidden;
    transition: border-color 140ms ease-out, transform 140ms ease-out, background 140ms ease-out;
  }
  .rh-welcome-channel:hover {
    border-color: var(--rh-coral-light);
    transform: translateY(-1px);
  }
  .rh-welcome-channel-link {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 14px;
    color: var(--rh-navy);
    text-decoration: none;
    cursor: pointer;
  }
  .rh-welcome-channel-icon {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: var(--rh-coral-bg);
    color: var(--rh-accent);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .rh-welcome-channel-icon svg { width: 16px; height: 16px; }
  .rh-welcome-channel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
  }
  .rh-welcome-channel-label {
    font-size: 14px;
    color: var(--rh-navy);
    line-height: 1.25;
  }
  .rh-welcome-channel-sub {
    font-size: 11px;
    color: var(--rh-muted);
    line-height: 1.3;
    /* Long emails ("support+ticketing@somelongbrandname.example.com")
       or roomy phone formats must not push the chevron off the right
       edge of the pill; ellipsis keeps the silhouette tight. */
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .rh-welcome-channel-chevron {
    color: var(--rh-muted);
    display: grid;
    place-items: center;
    flex-shrink: 0;
  }
  .rh-welcome-channel-chevron svg { width: 14px; height: 14px; }

  @keyframes rh-welcome-card-in {
    from { opacity: 0; transform: translateY(6px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* Loading shell shown briefly while /widget/v1/config is in flight. */
  .rh-welcome-loading-shell {
    flex: 1;
    padding: 18px 16px;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .rh-welcome-loading-strip {
    height: 64px;
    border-radius: 14px;
    background: linear-gradient(
      90deg,
      var(--rh-cream-deep) 0%,
      var(--rh-cream-warm) 50%,
      var(--rh-cream-deep) 100%
    );
    background-size: 200% 100%;
    animation: rh-welcome-shimmer 1400ms ease-in-out infinite;
  }
  .rh-welcome-loading-strip:nth-child(2) { height: 120px; animation-delay: 120ms; }
  .rh-welcome-loading-strip:nth-child(3) { height: 156px; animation-delay: 240ms; }
  @keyframes rh-welcome-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  /* ------------------------------------------------------------------
     Chat-state header refinements
     The chat surface header now shows a back arrow + agent avatar +
     agent name + "AI agent" tag, replacing the brand-only header
     from the pre-welcome design. Brand identity stays on the
     welcome card; the chat header focuses on who you are talking to.
  ------------------------------------------------------------------ */
  .rh-header-chat .rh-header-brand { gap: 6px; }
  .rh-back-btn { margin-left: -4px; }
  .rh-chat-agent {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
  }
  .rh-chat-agent-avatar {
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: var(--rh-coral-soft);
    color: var(--rh-accent);
    display: grid;
    place-items: center;
    font-size: 13px;
    font-weight: 600;
    overflow: hidden;
    flex-shrink: 0;
    box-shadow: inset 0 0 0 1px rgba(255, 92, 57, 0.12);
  }
  .rh-chat-agent-avatar img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .rh-header-agent-tag {
    font-size: 9.5px;
    color: var(--rh-muted);
    text-transform: uppercase;
    letter-spacing: 0.10em;
    font-weight: 600;
    margin-top: 1px;
  }

  /* Chat empty state: agent's first bubble + suggested prompts. The
     bubble itself uses the same rh-bubble styling as a streamed
     turn, so the visitor's mental model "this is what a reply looks
     like" is established from message zero. */
  .rh-empty-chat {
    display: flex;
    flex-direction: column;
    gap: 10px;
    animation: rh-welcome-card-in 220ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  /* ------------------------------------------------------------------
     No-source email-capture form
     Follows the canned escalation bubble. Visually it sits on the
     assistant side of the thread, but it is rendered as a soft
     cream card (no chat-bubble tail) so the visitor reads it as
     "Yara is asking you something" rather than "another message
     from Yara". The composer-textarea aesthetic is reused so the
     affordance feels native. Single-line layout on a wide bubble,
     stacks gracefully on narrow.
  ------------------------------------------------------------------ */
  .rh-no-source-form {
    max-width: 88%;
    padding: 12px 14px 12px;
    border-radius: var(--rh-radius-bubble);
    background: var(--rh-cream-warm);
    border: 1px solid var(--rh-divider-soft);
    border-bottom-left-radius: 4px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    animation: rh-token-fade-in 220ms ease-out both;
  }
  .rh-no-source-title {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: var(--rh-navy);
    line-height: 1.35;
  }
  .rh-no-source-body {
    margin: 0;
    font-size: 13px;
    color: var(--rh-charcoal);
    line-height: 1.5;
    opacity: 0.88;
  }
  .rh-no-source-row {
    display: flex;
    align-items: stretch;
    gap: 6px;
    margin-top: 2px;
  }
  .rh-no-source-input {
    flex: 1;
    min-width: 0;
    border: 1px solid var(--rh-divider);
    border-radius: 10px;
    background: var(--rh-white);
    padding: 8px 10px;
    font: inherit;
    font-size: 13.5px;
    color: var(--rh-navy);
    outline: none;
    transition: border-color 120ms ease-out, box-shadow 120ms ease-out;
  }
  .rh-no-source-input::placeholder { color: var(--rh-muted); }
  .rh-no-source-input:focus {
    border-color: var(--rh-coral-light);
    box-shadow: 0 0 0 3px rgba(255, 92, 57, 0.10);
  }
  .rh-no-source-input:disabled { opacity: 0.6; cursor: not-allowed; }
  .rh-no-source-send {
    background: var(--rh-navy);
    color: var(--rh-white);
    border: none;
    border-radius: 10px;
    padding: 0 14px;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: filter 120ms ease-out;
  }
  .rh-no-source-send:hover:not(:disabled) { filter: brightness(1.18); }
  .rh-no-source-send:disabled { opacity: 0.45; cursor: not-allowed; }
  .rh-no-source-error {
    margin: 2px 0 0;
    font-size: 12px;
    color: #B14B36;
    line-height: 1.4;
  }
  .rh-no-source-thanks {
    max-width: 88%;
    padding: 10px 14px;
    border-radius: var(--rh-radius-bubble);
    border-bottom-left-radius: 4px;
    background: var(--rh-cream-warm);
    border: 1px solid var(--rh-divider-soft);
    color: var(--rh-navy);
    font-size: 13.5px;
    line-height: 1.5;
    animation: rh-token-fade-in 220ms ease-out both;
  }

  /* ------------------------------------------------------------------
     Mobile bottom-sheet (under 600px viewport).
  ------------------------------------------------------------------ */
  @media (max-width: 600px) {
    .rh-panel {
      right: 0;
      left: 0;
      bottom: 0;
      width: 100%;
      max-width: none;
      height: 88vh;
      max-height: 88vh;
      border-radius: 22px 22px 0 0;
      animation: rh-sheet-in 240ms cubic-bezier(0.22, 1, 0.36, 1);
      transform-origin: bottom center;
    }
    .rh-panel-handle { display: grid; }
    @keyframes rh-sheet-in {
      from { transform: translateY(28px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    .rh-launcher { right: 16px; bottom: 16px; }
  }

  /* ------------------------------------------------------------------
     Voice modal
     A full-bleed overlay inside the panel. Cream wash, single
     breathing orb, audio waveform under the title when live, big
     navy stop pill. The intent: a phone call with a person, not a
     Siri overlay.
  ------------------------------------------------------------------ */
  .rh-voice-modal {
    position: absolute;
    inset: 0;
    z-index: 5;
    display: flex;
    flex-direction: column;
    background:
      radial-gradient(120% 80% at 50% 0%, var(--rh-coral-soft) 0%, transparent 60%),
      var(--rh-cream);
    border-radius: var(--rh-radius);
    animation: rh-voice-modal-in 240ms cubic-bezier(0.22, 1, 0.36, 1);
    overflow: hidden;
  }
  @keyframes rh-voice-modal-in {
    from { opacity: 0; transform: scale(0.98); }
    to { opacity: 1; transform: scale(1); }
  }
  .rh-voice-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 16px;
    flex-shrink: 0;
  }
  .rh-voice-back {
    background: transparent;
    border: none;
    color: var(--rh-muted);
    cursor: pointer;
    font: inherit;
    font-size: 12px;
    padding: 6px 10px 6px 6px;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: color 120ms ease-out, background 120ms ease-out;
  }
  .rh-voice-back:hover { color: var(--rh-navy); background: rgba(10, 22, 40, 0.04); }
  .rh-voice-back svg { width: 14px; height: 14px; }
  .rh-voice-status {
    font-size: 11px;
    color: var(--rh-muted);
    text-transform: uppercase;
    letter-spacing: 0.08em;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .rh-voice-status-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rh-muted);
    transition: background 200ms ease-out;
  }
  .rh-voice-status.live .rh-voice-status-dot {
    background: #4F9D69;
    box-shadow: 0 0 0 3px rgba(79, 157, 105, 0.18);
    animation: rh-live-dot 1600ms ease-in-out infinite;
  }
  @keyframes rh-live-dot {
    0%, 100% { box-shadow: 0 0 0 3px rgba(79, 157, 105, 0.18); }
    50% { box-shadow: 0 0 0 6px rgba(79, 157, 105, 0.04); }
  }
  .rh-voice-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 16px 24px 32px;
    gap: 22px;
    text-align: center;
    min-height: 0;
  }
  .rh-voice-title {
    font-size: 22px;
    line-height: 1.25;
    margin: 0;
    font-weight: 600;
    color: var(--rh-navy);
    letter-spacing: -0.01em;
  }
  .rh-voice-message {
    margin: 0;
    color: var(--rh-charcoal);
    font-size: 13.5px;
    line-height: 1.55;
    max-width: 280px;
    opacity: 0.88;
  }

  /* The orb. A single concentric-ring SVG sitting in a soft halo. The
     rings breathe via CSS keyframes; pinning the breathing to CSS
     keeps the SDK preload free of UI work. */
  .rh-voice-orb {
    position: relative;
    width: 168px;
    height: 168px;
    display: grid;
    place-items: center;
    margin: 4px 0;
  }
  .rh-voice-orb-halo {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background: radial-gradient(circle at center,
      rgba(255, 92, 57, 0.16) 0%,
      rgba(255, 92, 57, 0.06) 40%,
      transparent 70%);
    pointer-events: none;
  }
  .rh-voice-orb-ring {
    position: absolute;
    border-radius: 50%;
    border: 1px solid var(--rh-accent);
    opacity: 0;
    pointer-events: none;
  }
  .rh-voice-orb-active .rh-voice-orb-ring-1 {
    inset: 8px;
    animation: rh-voice-ring-1 2800ms cubic-bezier(0.4, 0, 0.6, 1) infinite;
  }
  .rh-voice-orb-active .rh-voice-orb-ring-2 {
    inset: 8px;
    animation: rh-voice-ring-1 2800ms cubic-bezier(0.4, 0, 0.6, 1) infinite 900ms;
  }
  .rh-voice-orb-active .rh-voice-orb-ring-3 {
    inset: 8px;
    animation: rh-voice-ring-1 2800ms cubic-bezier(0.4, 0, 0.6, 1) infinite 1800ms;
  }
  @keyframes rh-voice-ring-1 {
    0% { transform: scale(0.94); opacity: 0; }
    20% { opacity: 0.55; }
    100% { transform: scale(1.18); opacity: 0; }
  }
  .rh-voice-orb-core {
    position: relative;
    width: 104px;
    height: 104px;
    border-radius: 50%;
    background:
      radial-gradient(circle at 35% 30%, var(--rh-coral-light) 0%, var(--rh-accent) 60%, #E0492A 100%);
    color: var(--rh-white);
    display: grid;
    place-items: center;
    cursor: pointer;
    border: none;
    box-shadow:
      0 1px 2px rgba(10, 22, 40, 0.08),
      0 12px 28px rgba(255, 92, 57, 0.34),
      inset 0 -2px 8px rgba(10, 22, 40, 0.10),
      inset 0 2px 6px rgba(255, 255, 255, 0.30);
    transition: transform 160ms ease-out, box-shadow 160ms ease-out;
  }
  .rh-voice-orb-core:hover:not(:disabled) {
    transform: translateY(-1px) scale(1.02);
    box-shadow:
      0 2px 4px rgba(10, 22, 40, 0.10),
      0 18px 36px rgba(255, 92, 57, 0.42),
      inset 0 -2px 8px rgba(10, 22, 40, 0.10),
      inset 0 2px 6px rgba(255, 255, 255, 0.30);
  }
  .rh-voice-orb-core:active:not(:disabled) { transform: translateY(0) scale(1); }
  .rh-voice-orb-core:disabled {
    cursor: default;
    /* When live we keep the gradient but stop the press affordance. */
  }
  .rh-voice-orb-core svg { width: 36px; height: 36px; }
  .rh-voice-orb-core.live {
    cursor: default;
    animation: rh-voice-breathe 3200ms ease-in-out infinite;
  }
  @keyframes rh-voice-breathe {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.04); }
  }

  /* Audio waveform shown when live. Six tiny bars, staggered so it
     feels like a live spectrum without us actually wiring up the
     mic stream (which the SDK owns). */
  .rh-voice-bars {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    height: 16px;
  }
  .rh-voice-bars span {
    width: 3px;
    height: 6px;
    background: var(--rh-accent);
    border-radius: 2px;
    opacity: 0.85;
    animation: rh-voice-bar 900ms ease-in-out infinite;
  }
  .rh-voice-bars span:nth-child(1) { animation-delay: 0ms; }
  .rh-voice-bars span:nth-child(2) { animation-delay: 120ms; }
  .rh-voice-bars span:nth-child(3) { animation-delay: 240ms; }
  .rh-voice-bars span:nth-child(4) { animation-delay: 360ms; }
  .rh-voice-bars span:nth-child(5) { animation-delay: 480ms; }
  .rh-voice-bars span:nth-child(6) { animation-delay: 200ms; }
  @keyframes rh-voice-bar {
    0%, 100% { height: 4px; opacity: 0.6; }
    50% { height: 14px; opacity: 1; }
  }

  /* Stop button: navy filled square in a navy pill. Immediately reads
     as "end the call". */
  .rh-voice-end {
    background: var(--rh-navy);
    color: var(--rh-white);
    border: none;
    cursor: pointer;
    padding: 10px 18px 10px 14px;
    border-radius: 999px;
    font: inherit;
    font-size: 13px;
    font-weight: 500;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: filter 140ms ease-out, transform 140ms ease-out;
    box-shadow: 0 4px 14px rgba(10, 22, 40, 0.22);
  }
  .rh-voice-end:hover { filter: brightness(1.18); transform: translateY(-1px); }
  .rh-voice-end:active { transform: translateY(0); }
  .rh-voice-end-square {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    background: var(--rh-coral);
    display: inline-block;
    flex-shrink: 0;
  }

  /* Voice idle hint: small "tap the orb to start" line under the
     description. Subtle, doesn't compete with the body copy. */
  .rh-voice-hint {
    font-size: 11px;
    color: var(--rh-muted);
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.10em;
    font-weight: 500;
  }

  /* Respect users who reduced motion in their OS. */
  @media (prefers-reduced-motion: reduce) {
    .rh-panel, .rh-cursor, .rh-typing-dots span, .rh-token-fade,
    .rh-launcher, .rh-launcher-online-dot, .rh-icon-btn, .rh-composer textarea,
    .rh-send, .rh-mic-primary,
    .rh-voice-modal, .rh-voice-orb-core, .rh-voice-orb-ring,
    .rh-voice-bars span, .rh-tool-line::before, .rh-empty-chat,
    .rh-welcome-greeting, .rh-welcome-chat-card, .rh-welcome-channels,
    .rh-welcome-loading-strip,
    .rh-voice-status.live .rh-voice-status-dot {
      animation: none !important;
      transition: none !important;
    }
  }
`;
